import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import { router, type Href } from 'expo-router';
import React, { useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';

import { ApplicationAttachments } from './application-attachments';
import { ApplicationChecklist } from './application-checklist';
import { ApplicationEditModal } from './application-edit-modal';
import { AttachmentMetadataModal } from './attachment-metadata-modal';
import { StatusTransitionButtons } from './status-buttons';

import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { DateDisplay } from '@/components/common/date-display';
import { useComposer } from '@/components/composers/composer-host';
import { DateInput } from '@/components/form/date-input';
import { AutoReplyChip } from '@/components/inbox/auto-reply-notice';
import { useNotification } from '@/components/notification-system';
import { PrintButton } from '@/components/print-button';
import { ActivityIndicator, Button, Divider, Modal, Text, TextInput, useTheme } from '@/components/ui';
import { useApi } from '@/hooks/use-api';
import {
	useAddApplicationAttachment, useApplication, useDeleteAttachment, useMergeApplication,
	useUpdateApplication, useUpdateAttachmentMetadata,
} from '@/hooks/use-data';
import { usePermissions } from '@/hooks/use-permissions';
import type { MessageAttachment, SiblingRequest } from '@/services/data-service';
import { Alert } from '@/utils/alert';
import { friendlyApiMessage } from '@/utils/api-error';
import { canLogApplicationDecision } from '@/utils/application-decision';
import {
	applicationReopenTarget, applicationStatusLabel, canSendBoardApprovalLetter, ExteriorApplicationStatus,
} from '@/utils/application-status';
import { applicationStatusStyle } from '@/utils/application-status-style';
import { etDayMs, formatBusinessDate, toBusinessMs } from '@/utils/date-utils';
import { letterDeliveryAttempt } from '@/utils/message-delivery';
import { pickedDocumentFormData } from '@/utils/native-form-file';
import { backOrApplications } from '@/utils/router-back';

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<View style={{ flexDirection: 'row', marginBottom: 8, flexWrap: 'wrap' }}>
		<Text style={{ fontWeight: '600', width: 150, color: '#555' }}>{label}</Text>
		<View style={{ flex: 1, minWidth: 160 }}>{children}</View>
	</View>
);

/**
 * One improvement request: the workflow (#369).
 *
 * The conversation is a list of LINKED MESSAGES, not correspondence the request owns —
 * each row navigates to that message's own permanent /inbox/<id>. That separation is
 * what lets an email belong to a request without disappearing from the inbox.
 */
export const ApplicationDetail: React.FC<{ applicationId: string; fullScreen?: boolean }> = ({
	applicationId, fullScreen = false,
}) => {
	const theme = useTheme();
	const { uploadFileToServer } = useApi();
	const { showModal, showToast } = useNotification();
	const permissions = usePermissions();
	const canUpdate = permissions.canUseInbox;

	const { data: application, isLoading } = useApplication(applicationId);
	const mergeApplication = useMergeApplication();
	const updateAttachment = useUpdateAttachmentMetadata();
	const [editingAttachment, setEditingAttachment] = useState<MessageAttachment | null>(null);
	const [editingRequest, setEditingRequest] = useState(false);
	const [mergeInto, setMergeInto] = useState<SiblingRequest | null>(null);
	const updateApplication = useUpdateApplication();
	const deleteAttachment = useDeleteAttachment();
	const addApplicationAttachment = useAddApplicationAttachment();
	// Own state rather than the mutation's `isPending`: the upload to R2 happens BEFORE the
	// mutation starts, and on a phone photo that is most of the wait.
	const [uploading, setUploading] = useState(false);

	const [decisionStatus, setDecisionStatus] = useState<ExteriorApplicationStatus | null>(null);
	const [confirmingReopen, setConfirmingReopen] = useState(false);
	/**
	 * The letter composer is NOT rendered here — `ComposerProvider` owns it at the app root.
	 *
	 * Rendered by this page it died with the page: open a request, start the letter, minimize
	 * it, click anywhere else, and a half-written explanation to a homeowner was gone. A
	 * `Portal` moves where the window paints, not where it lives.
	 */
	const composer = useComposer();
	const openNeedsInfo = () => composer.open({ kind: 'needs-info', applicationId });
	const openBoardApproval = () => composer.open({ kind: 'board-approval', applicationId });
	const [decisionNotes, setDecisionNotes] = useState('');
	const [boardMeetingDate, setBoardMeetingDate] = useState<Date | null>(null);

	// Mirrors `canLogDecision` in api/exterior-applications.ts so the UI never offers a button the
	// API will refuse.
	const canLogDecision = canLogApplicationDecision(permissions);

	if (isLoading) {
		return (
			<View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
				<ActivityIndicator />
			</View>
		);
	}
	if (!application) {
		return (
			<View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32 }}>
				<Ionicons name="alert-circle-outline" size={40} color={theme.colors.outline} />
				<Text style={{ marginTop: 12, color: theme.colors.onSurfaceVariant }}>This request no longer exists.</Text>
			</View>
		);
	}

	const fail = (error: unknown, fallback: string) =>
		showModal({ title: 'Error', message: friendlyApiMessage(error, fallback), variant: 'error' });

	/**
	 * Confirm before removing: a file that arrived by email exists nowhere else, so this is
	 * the last copy. Named in the prompt so nobody deletes the wrong one from a wall of
	 * near-identical thumbnails.
	 */
	const removeAttachment = (attachment: { id: string; fileName: string }) => {
		Alert.alert(
			'Remove this file?',
			// Says "everywhere" because it is a global soft delete on the attachment row, not
			// an unlink from this request: the file also disappears from the message it
			// arrived on, and from any other request that message is linked to.
			//
			// A confirmation prompt exists to inform ONE decision: whether this is
			// recoverable. It is — the row is soft-deleted and the R2 object is retained —
			// but only an admin can reverse it, so the prompt has to say so rather than
			// implying staff can undo it themselves.
			`"${attachment.fileName}" will be removed from this request and from the email it `
			+ 'arrived on. This is a soft delete — ask an admin if you need it back.',
			[
				{ text: 'Cancel', style: 'cancel' },
				{
					text: 'Remove',
					style: 'destructive',
					onPress: async () => {
						try {
							await deleteAttachment.mutateAsync(attachment.id);
							showToast(`Removed ${attachment.fileName}`, { variant: 'success' });
						} catch (error) {
							fail(error, 'Could not remove that file.');
						}
					},
				},
			],
		);
	};

	/**
	 * Two hops on purpose: the bytes go to `/files` (the shared upload transport, with its
	 * retry and long timeout), then the metadata to the request, which creates the row.
	 * Combining them into one multipart call would mean re-implementing that retry inside a
	 * feature endpoint.
	 */
	const addAttachment = async () => {
		const picked = await DocumentPicker.getDocumentAsync({ multiple: false, copyToCacheDirectory: true });
		if (picked.canceled || !picked.assets?.[0]) return;
		const asset = picked.assets[0];

		setUploading(true);
		try {
			// applicationId scopes the R2 key to this request; `/files` accepts it as a known scope.
			const formData = await pickedDocumentFormData(asset, { attachmentType: 'misc', applicationId });

			const uploaded = await uploadFileToServer(formData);
			if (!uploaded?.storageKey) throw new Error('The file did not upload.');

			const result = await addApplicationAttachment.mutateAsync({
				applicationId,
				storageKey: uploaded.storageKey,
				fileName: uploaded.fileName ?? asset.name,
				mimeType: uploaded.mimeType ?? asset.mimeType ?? undefined,
				fileSize: uploaded.fileSize ?? asset.size ?? undefined,
			});

			showToast(`Added ${result.fileName}`, { variant: 'success' });
		} catch (error) {
			fail(error, 'Could not add that file.');
		} finally {
			setUploading(false);
		}
	};

	const transition = async (next: ExteriorApplicationStatus) => {
		try {
			await updateApplication.mutateAsync({ id: applicationId, data: { status: next } });
			showToast(applicationStatusLabel(next), { variant: 'success' });
		} catch (error) {
			fail(error, 'Could not update the status.');
		}
	};

	/**
	 * Every closing dialog opens blank. Cancel only clears the status, so notes typed for an
	 * abandoned Approve/Reject would otherwise ride into Withdrawn and satisfy its required reason.
	 */
	const openClosingDialog = (status: ExteriorApplicationStatus) => {
		setDecisionNotes('');
		setBoardMeetingDate(null);
		setDecisionStatus(status);
	};
	const withdrawing = decisionStatus === ExteriorApplicationStatus.Withdrawn;
	const confirmDecision = async () => {
		if (decisionStatus == null) return;
		try {
			await updateApplication.mutateAsync({
				id: applicationId,
				data: {
					status: decisionStatus,
					decisionNotes: decisionNotes.trim() || undefined,
					// A withdrawal never met the board, so it must not overwrite a meeting date.
					...(withdrawing ? {} : {
						// DateInput emits 05:00Z — snap to the canonical midnight-UTC
						// business-date grid rather than persisting the raw instant.
						boardMeetingDate: boardMeetingDate ? toBusinessMs(boardMeetingDate) : null,
					}),
				},
			});
			setDecisionStatus(null);
			setDecisionNotes('');
			setBoardMeetingDate(null);
		} catch (error) {
			fail(error, 'Could not log the decision.');
		}
	};

	/**
	 * Undo a decision or withdrawal: back to the closed status's reopen target. A plain status
	 * PUT — NOT the board letter the ordinary move to Waiting for Board Approval opens: the board
	 * has already had a decided request, and the server clears the stamp and checks the caller.
	 */
	const reopenTarget = applicationReopenTarget(application.status);
	const reopen = async () => {
		if (reopenTarget == null) return;
		try {
			await updateApplication.mutateAsync({ id: applicationId, data: { status: reopenTarget } });
			setConfirmingReopen(false);
			showToast(applicationStatusLabel(reopenTarget), { variant: 'success' });
		} catch (error) {
			fail(error, 'Could not reopen the request.');
		}
	};
	// The decision's America/New_York business day — the same day the decision line and PDF use.
	const decidedOn = application.decidedAt != null ? ` on ${formatBusinessDate(etDayMs(application.decidedAt))}` : '';
	const closedBy = `${application.decidedByName ?? 'a former user'}${decidedOn}`;
	const reopenMessage = application.status === ExteriorApplicationStatus.Withdrawn
		? `Move this request back to ${applicationStatusLabel(ExteriorApplicationStatus.Submitted)}? `
			+ `This clears the withdrawal by ${closedBy}.`
		: 'Move this request back to Waiting for Board Approval? This clears the '
			+ `${application.statusLabel.toLowerCase()} decision by ${closedBy} — the board will need to decide again.`;

	const statusColor = applicationStatusStyle(application.status).backgroundColor;
	const overdue = application.reviewDeadline != null && application.reviewDeadline < Date.now();

	return (
		<>
			<ScrollView style={{ flex: 1, padding: 16 }} testID="application-detail">
				<View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
					<Pressable
						onPress={() => backOrApplications(application.associationId, application.accountId)}
						testID="btn-back-application"
						// Real padding, not hitSlop — RN-Web drops hitSlop entirely.
						style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingRight: 12 }}
					>
						<Ionicons name="arrow-back" size={18} color={theme.colors.primary} style={{ marginRight: 6 }} />
						<Text style={{ color: theme.colors.primary, fontWeight: '600' }}>Requests</Text>
					</Pressable>
					<View style={{ flex: 1 }} />
					{/*
						Full screen drops the rail and the list for the one case they get in the
						way: reading a long request, or showing it to a board. A DISTINCT route,
						not a param on this one — pushing the same route with different params
						mounts a second screen instead of making a history entry, so the back
						button would stop working.
					*/}
					{canUpdate ? (
						<Pressable
							onPress={() => setEditingRequest(true)}
							testID="btn-edit-application"
							style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: 10 }}
						>
							<Ionicons name="create-outline" size={18} color={theme.colors.primary} style={{ marginRight: 6 }} />
							<Text style={{ color: theme.colors.primary, fontWeight: '600' }}>Edit</Text>
						</Pressable>
					) : null}
					{/*
						The entry point that does NOT depend on a status transition.
						The composer was reachable only through the "Needs Additional Info"
						status button, so a request with no legal move to that status — an
						Approved one, say — could not be emailed at all. Writing to a homeowner
						is not a status change and should not have to borrow one's button.
					*/}
					{canUpdate ? (
						<Pressable
							onPress={openNeedsInfo}
							testID="btn-email-homeowner"
							style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: 10 }}
						>
							<Ionicons name="mail-outline" size={18} color={theme.colors.primary} style={{ marginRight: 6 }} />
							<Text style={{ color: theme.colors.primary, fontWeight: '600' }}>Email</Text>
						</Pressable>
					) : null}
					{/*
						The same composer the "Waiting for Board Approval" status button opens,
						reachable without that button — to resend the letter to a request already
						with the board. Hidden once decided: "it's with the board" is false by then.
					*/}
					{canUpdate && canSendBoardApprovalLetter(application.status) ? (
						<Pressable
							onPress={openBoardApproval}
							testID="btn-board-approval-letter"
							style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: 10 }}
						>
							<Ionicons name="people-outline" size={18} color={theme.colors.primary} style={{ marginRight: 6 }} />
							<Text style={{ color: theme.colors.primary, fontWeight: '600' }}>Board Approval</Text>
						</Pressable>
					) : null}
					{!fullScreen ? (
						<Pressable
							onPress={() => router.push(`/exterior-applications/full/${applicationId}` as Href)}
							testID="btn-fullscreen-application"
							style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: 10 }}
						>
							<Ionicons name="expand-outline" size={18} color={theme.colors.primary} style={{ marginRight: 6 }} />
							<Text style={{ color: theme.colors.primary, fontWeight: '600' }}>Full Screen</Text>
						</Pressable>
					) : null}
					{/* window.open, never router.push: a /print route pushed on web mis-resolves
					    to a sibling and spins forever. PrintButton already opens a tab. */}
					<PrintButton url={`/print/exterior-application/${applicationId}`} />
				</View>

				<View style={{ flexDirection: 'row', alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 4 }}>
					<View style={{ flex: 1, minWidth: 200, marginRight: 8 }}>
						<Text style={{ fontSize: 22, fontWeight: '700' }} numberOfLines={2}>
							{application.label || application.requestTypeName || 'Improvement Request'}
						</Text>
						{application.applicationNumber != null ? (
							<Text style={{ color: theme.colors.onSurfaceVariant }}>
								Request #{application.applicationNumber}
							</Text>
						) : null}
						{application.associationName ? (
							<Text style={{ color: theme.colors.onSurfaceVariant }}>{application.associationName}</Text>
						) : null}
					</View>
					<View style={{ paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12, backgroundColor: `${statusColor}22` }}>
						<Text style={{ fontSize: 12, fontWeight: '700', color: statusColor }}>{application.statusLabel}</Text>
					</View>
				</View>

				{canUpdate ? (
					<View style={{ marginTop: 16 }}>
						<Text style={{ fontWeight: '600', color: '#555' }}>Advance status</Text>
						<StatusTransitionButtons
							currentStatus={application.status}
							/*
								Two transitions owe the homeowner a letter, so they open a composer
								instead of moving the status on their own: Needs Additional Info, and
								Waiting for Board Approval (#555), whose status moves ONLY when the PM
								presses Send — closing the composer leaves the request as it was.
								Branching HERE rather than adding a callback keeps status-buttons.tsx
								unaware of which moves need a dialog; its job is to render the legal moves.
							*/
							onTransition={(next) => {
								if (next === ExteriorApplicationStatus.NeedsAdditionalInfo) return openNeedsInfo();
								if (next === ExteriorApplicationStatus.SubmittedToBoard) return openBoardApproval();
								return transition(next);
							}}
							onRequestDecision={openClosingDialog}
							onRequestReopen={() => setConfirmingReopen(true)}
							canLogDecision={canLogDecision}
							isSubmitting={updateApplication.isPending}
						/>
					</View>
				) : null}

				<Divider style={{ marginVertical: 16 }} />

				<Row label="Submitter">
					<Text>{application.submitterName || '—'}</Text>
					{application.submitterEmail ? (
						<Text style={{ color: theme.colors.onSurfaceVariant }}>{application.submitterEmail}</Text>
					) : null}
				</Row>
				<Row label="Property"><Text>{application.propertyAddress || '—'}</Text></Row>
				<Row label="Homeowner / Lot">
					{application.accountId ? (
						<Pressable
							onPress={() => router.push(`/accounting/account/${application.accountId}` as Href)}
							style={{ paddingVertical: 2 }}
							testID="link-application-account"
						>
							<Text style={{ color: theme.colors.primary }}>
								{application.accountOwnerName || 'Matched lot'}
								{application.accountNumber ? ` · ${application.accountNumber}` : ''}
							</Text>
						</Pressable>
					) : <Text style={{ color: theme.colors.error }}>Unmatched</Text>}
				</Row>
				<Row label="Request type"><Text>{application.requestTypeName || '—'}</Text></Row>
				{application.description ? <Row label="Description"><Text>{application.description}</Text></Row> : null}
				{application.reviewDeadline ? (
					<Row label="Review deadline">
						{/* Overdue is the one thing a reviewer must not miss, so it is coloured
							 rather than left as another date in a list. */}
						<Text style={{ color: overdue ? theme.colors.error : undefined, fontWeight: overdue ? '700' : '400' }}>
							<DateDisplay dateOnly date={new Date(application.reviewDeadline)} />
							{overdue ? '  overdue' : ''}
						</Text>
					</Row>
				) : null}

				{(application.decisionNotes || application.boardMeetingDate || application.decidedAt) ? (
					<>
						<Text style={{ fontSize: 18, fontWeight: '700', marginTop: 12, marginBottom: 8 }}>
							{application.status === ExteriorApplicationStatus.Withdrawn ? 'Withdrawal' : 'Board Decision'}
						</Text>
						{application.boardMeetingDate ? (
							<Row label="Board meeting"><DateDisplay businessDate date={new Date(application.boardMeetingDate)} /></Row>
						) : null}
						{application.decisionNotes ? <Row label="Notes"><Text>{application.decisionNotes}</Text></Row> : null}
						{application.decidedAt ? (
							<Row label={application.status === ExteriorApplicationStatus.Withdrawn ? 'Withdrawn' : 'Decided'}>
								<DateDisplay date={new Date(application.decidedAt)} />
							</Row>
						) : null}
					</>
				) : null}

				{/*
					Duplicate requests are the normal failure mode: the homeowner submits, hears
					nothing, and submits again. Offering the merge HERE — next to the request
					that is about to become the second review clock — is what keeps the two from
					living out separate lives.
				*/}
				{canUpdate && application.siblingRequests?.length ? (
					<>
						<Divider style={{ marginVertical: 16 }} />
						<Text style={{ fontSize: 18, fontWeight: '700', marginBottom: 4 }}>Other requests on this lot</Text>
						<Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 8 }}>
							Is this the same job? Merging folds THIS request into the one you pick and moves its
							messages across.
						</Text>
						{application.siblingRequests.map((sibling) => (
							<View
								key={sibling.id}
								testID={`sibling-request-${sibling.id}`}
								style={{
									flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', rowGap: 8,
									padding: 12, marginBottom: 8, borderRadius: 8, borderWidth: 1,
									borderColor: theme.colors.outlineVariant ?? theme.colors.outline,
								}}
							>
								<Pressable
									onPress={() => router.push(`/exterior-applications/${sibling.id}` as Href)}
									style={{ flex: 1, minWidth: 180, marginRight: 8, paddingVertical: 2 }}
								>
									<Text style={{ fontWeight: '700' }} numberOfLines={1}>
										{sibling.applicationNumber != null ? `#${sibling.applicationNumber} · ` : ''}
										{sibling.requestTypeName || 'Uncategorized'}
									</Text>
									<Text numberOfLines={2} style={{ color: theme.colors.onSurfaceVariant, marginTop: 2 }}>
										{sibling.description || '—'}
									</Text>
									<Text style={{ fontSize: 11, color: theme.colors.onSurfaceVariant, marginTop: 2 }}>{sibling.statusLabel}</Text>
								</Pressable>
								<Button
									mode="outlined"
									icon="git-merge-outline"
									onPress={() => setMergeInto(sibling)}
									testID={`btn-merge-into-${sibling.id}`}
								>
									Merge into this
								</Button>
							</View>
						))}
					</>
				) : null}

				{application.checklist?.length ? (
					<>
						<Divider style={{ marginVertical: 16 }} />
						<ApplicationChecklist
							items={application.checklist}
							complete={application.checklistComplete ?? false}
							canUpdate={canUpdate}
							busy={updateApplication.isPending}
							onToggle={async (key, checked) => {
								try {
									await updateApplication.mutateAsync({
										id: applicationId,
										data: {
											// Merge, never replace: sending only the toggled key would
											// wipe every other tick on the request.
											checklistState: {
												...Object.fromEntries((application.checklist ?? []).map((i) => [i.key, i.checked])),
												[key]: checked,
											},
										},
									});
								} catch (error) {
									fail(error, 'Could not update the checklist.');
								}
							}}
						/>
					</>
				) : null}

				{/*
					Rendered whenever the reader may edit, even with nothing attached — the Add
					button lives in this section's header, so hiding the section on an empty
					request would hide the only way to put the first file on it.
				*/}
				{(application.attachments?.length || canUpdate) ? (
					<>
						<Divider style={{ marginVertical: 16 }} />
						<ApplicationAttachments
							attachments={application.attachments ?? []}
							onError={fail}
							onEdit={canUpdate ? setEditingAttachment : undefined}
							onRemove={canUpdate ? removeAttachment : undefined}
							onAdd={canUpdate ? addAttachment : undefined}
							adding={uploading}
						/>
					</>
				) : null}

				<Divider style={{ marginVertical: 16 }} />

				{/*
					Linked messages, not owned correspondence. Each row goes to that message's
					own /inbox/<id> — an email can belong to this request AND stay addressable
					in the inbox, which is the whole reason the two are separate.
				*/}
				<Text style={{ fontSize: 18, fontWeight: '700', marginBottom: 8 }}>
					Messages{application.messageCount ? ` (${application.messageCount})` : ''}
				</Text>
				{application.messages?.length ? (
					application.messages.map((message) => (
						<Pressable
							key={message.id}
							onPress={() => router.push(`/inbox/${message.id}` as Href)}
							testID={`application-message-${message.id}`}
							style={{
								padding: 12,
								marginBottom: 8,
								borderRadius: 8,
								borderWidth: 1,
								borderColor: theme.colors.outlineVariant ?? theme.colors.outline,
							}}
						>
							<View style={{ flexDirection: 'row', alignItems: 'center' }}>
								<Text style={{ fontWeight: '700', flex: 1, minWidth: 0 }} numberOfLines={1}>
									{message.fromName || message.fromAddress || 'Unknown sender'}
									{message.relation === 'origin' ? '  · started this request' : ''}
								</Text>
								{/* A letter recorded here but never delivered must not read as sent (#555). */}
								<AutoReplyChip
									info={letterDeliveryAttempt(message)}
									noun="Letter"
									testID={`application-message-${message.id}-delivery`}
								/>
								{message.createdAt ? (
									<DateDisplay date={new Date(message.createdAt)} dateOnly variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }} />
								) : null}
							</View>
							<Text numberOfLines={2} style={{ color: theme.colors.onSurfaceVariant, marginTop: 4 }}>
								{message.subject || message.bodyText || '—'}
							</Text>
						</Pressable>
					))
				) : <Text style={{ color: '#888', fontStyle: 'italic' }}>No messages linked yet.</Text>}
			</ScrollView>

			{/*
				Confirmed, never one-click: a merge retires an application number for good and
				is not undoable from the UI.
			*/}
			{/*
				"Email the homeowner" renders in `ComposerProvider`, not here — see
				`openNeedsInfo` above. Its three-outcome reporting moved with it.
			*/}

			<ApplicationEditModal
				application={editingRequest ? application : null}
				busy={updateApplication.isPending}
				onDismiss={() => setEditingRequest(false)}
				onSave={async (values) => {
					try {
						await updateApplication.mutateAsync({ id: applicationId, data: values });
						setEditingRequest(false);
						showToast('Request updated', { variant: 'success' });
					} catch (error) {
						fail(error, 'Could not update the request.');
					}
				}}
			/>

			<AttachmentMetadataModal
				attachment={editingAttachment}
				checklist={application.checklist ?? []}
				busy={updateAttachment.isPending}
				onDismiss={() => setEditingAttachment(null)}
				onSave={async (values) => {
					if (!editingAttachment) return;
					try {
						await updateAttachment.mutateAsync({ id: editingAttachment.id, ...values });
						setEditingAttachment(null);
						showToast('File updated', { variant: 'success' });
					} catch (error) {
						fail(error, 'Could not update that file.');
					}
				}}
			/>

			<Modal visible={mergeInto != null} onDismiss={() => setMergeInto(null)}>
				<View style={{ padding: 20, maxWidth: 420 }}>
					<Text style={{ fontSize: 18, fontWeight: '700', marginBottom: 8 }}>Merge this request?</Text>
					<Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 12 }}>
						{`This request${application.applicationNumber != null ? ` (#${application.applicationNumber})` : ''} will be folded into `}
						<Text style={{ fontWeight: '700' }}>
							{mergeInto?.applicationNumber != null ? `#${mergeInto.applicationNumber}` : 'the selected request'}
						</Text>
						{'. Its messages move across and keep their own inbox links. The merged request\u2019s number is retired.'}
					</Text>
					<View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8 }}>
						<Button mode="outlined" onPress={() => setMergeInto(null)}>Cancel</Button>
						<Button
							mode="contained"
							loading={mergeApplication.isPending}
							disabled={mergeApplication.isPending}
							testID="btn-confirm-merge"
							onPress={async () => {
								if (!mergeInto) return;
								const target = mergeInto;
								try {
									await mergeApplication.mutateAsync({ id: applicationId, intoId: target.id });
									setMergeInto(null);
									showToast('Merged', { variant: 'success' });
									// The record the user is standing on no longer exists — follow the survivor.
									router.replace(`/exterior-applications/${target.id}` as Href);
								} catch (error) {
									showToast(friendlyApiMessage(error, 'Could not merge these requests.'), { variant: 'error' });
								}
							}}
						>
							Merge
						</Button>
					</View>
				</View>
			</Modal>

			<ConfirmDialog
				visible={confirmingReopen}
				onDismiss={() => setConfirmingReopen(false)}
				title="Reopen this request?"
				content={reopenMessage}
				confirmLabel="Reopen"
				onConfirm={reopen}
				confirmLoading={updateApplication.isPending}
				danger
			/>

			<Modal visible={decisionStatus != null} onDismiss={() => setDecisionStatus(null)}>
				<View style={{ padding: 20, minWidth: 320 }}>
					<Text style={{ fontSize: 18, fontWeight: '700', marginBottom: 4 }}>
						{decisionStatus != null ? applicationStatusLabel(decisionStatus) : ''}
					</Text>
					<Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 12 }}>
						{withdrawing
							? 'Close this request without a board decision and move it to Archived. Say why — the note is '
								+ 'the only record of it. No decision PDF is filed. Only someone who can log decisions can '
								+ 'undo it, by reopening the request.'
							: 'Log the board\'s decision. This stamps who decided and when. Only someone who can log '
								+ 'decisions can undo it, by reopening the request.'}
					</Text>
					{withdrawing ? null : (
						<DateInput label="Board meeting date" value={boardMeetingDate} onChange={setBoardMeetingDate} />
					)}
					<TextInput
						label={withdrawing ? 'Reason (required)' : 'Decision notes'}
						value={decisionNotes}
						onChangeText={setDecisionNotes}
						multiline
						numberOfLines={4}
						testID="decision-notes"
					/>
					<View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
						<Button mode="outlined" onPress={() => setDecisionStatus(null)}>Cancel</Button>
						<Button
							mode="contained"
							variant={decisionStatus === ExteriorApplicationStatus.Approved ? 'success' : 'error'}
							onPress={confirmDecision}
							disabled={updateApplication.isPending || (withdrawing && !decisionNotes.trim())}
							loading={updateApplication.isPending}
							testID="btn-confirm-decision"
						>
							Confirm
						</Button>
					</View>
				</View>
			</Modal>
		</>
	);
};

export default ApplicationDetail;
